import React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MaterialSymbol from '../MaterialSymbol';
import AccountMenu from './AccountMenu';
import { MY_TICKETS_TREATMENT, MANAGE_EVENTS_TREATMENT } from './accountDestinationTreatments';
import { accountActionLinkProps } from './buildAccountActions';
import { accountShape, headerTranslationsShape } from './propTypes';
import { pickForHighContrast } from '../highContrastSx';

function buildLinkProps(href) {
    if (!href) {
        return { disabled: true };
    }

    return accountActionLinkProps({ href, opensNewTab: true });
}

const HOVER_TINT_BG = 'primary.light';
const BUTTON_ICON_SIZE = 16;

const HOVERED_INK = 'primary.contrastText';

function buildButtonSx(treatment) {
    const hoveredInk = pickForHighContrast(HOVERED_INK, treatment.iconColor);

    return {
        whiteSpace: 'nowrap',
        borderColor: treatment.iconColor,
        color: treatment.iconColor,
        '&:hover': {
            bgcolor: pickForHighContrast(treatment.iconColor, HOVER_TINT_BG),
            borderColor: treatment.iconColor,
            color: hoveredInk,
            '& .MuiButton-startIcon': {
                color: hoveredInk,
            },
        },
        '& .MuiButton-startIcon': {
            color: treatment.iconColor,
        },
    };
}

const MY_TICKETS_BUTTON_SX = buildButtonSx(MY_TICKETS_TREATMENT);
const MANAGE_EVENTS_BUTTON_SX = buildButtonSx(MANAGE_EVENTS_TREATMENT);

const ACTIONS_DIVIDER_SX = {
    height: 20,
    alignSelf: 'center',
    mx: 0.5,
    borderColor: 'grey.200',
};

function HeaderActions({
    account = null,
    myTicketsUrl = '',
    manageEventsUrl = '',
    translations,
    localeSelectors = null,
}) {
    const signedIn = account !== null;

    const accountControls = signedIn ? (
        <AccountMenu
            account={account}
            myTicketsUrl={myTicketsUrl}
            manageEventsUrl={manageEventsUrl}
            translations={translations}
        />
    ) : (
        <>
            <Button
                {...buildLinkProps(myTicketsUrl)}
                variant="outlined"
                startIcon={<MaterialSymbol name={MY_TICKETS_TREATMENT.icon} size={BUTTON_ICON_SIZE} />}
                sx={MY_TICKETS_BUTTON_SX}
                data-testid="redesign-header-my-tickets"
            >
                {translations.myTickets}
            </Button>
            <Button
                {...buildLinkProps(manageEventsUrl)}
                variant="outlined"
                startIcon={<MaterialSymbol name={MANAGE_EVENTS_TREATMENT.icon} size={BUTTON_ICON_SIZE} />}
                sx={MANAGE_EVENTS_BUTTON_SX}
                data-testid="redesign-header-manage-events"
            >
                {translations.manageEvents}
            </Button>
        </>
    );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {signedIn ? localeSelectors : accountControls}
            {localeSelectors === null ? null : (
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={ACTIONS_DIVIDER_SX}
                    data-testid="header-actions-divider"
                />
            )}
            {signedIn ? accountControls : localeSelectors}
        </Stack>
    );
}

HeaderActions.propTypes = {
    account: accountShape,
    myTicketsUrl: PropTypes.string,
    manageEventsUrl: PropTypes.string,
    translations: headerTranslationsShape.isRequired,
    localeSelectors: PropTypes.node,
};

export default HeaderActions;
